import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { MenuPath, Note } from '../../touchpose/ui';
import {
  H2,
  Body,
  DocHero,
  KeySettings,
  QuadCages,
  SequencerNote,
  DocFooter,
  KeyCombo,
  Steps,
  UL_CLASS
} from './shared';

const M = (s: string) => <MenuPath>{s}</MenuPath>;

const COMPONENT = 'Squarebit Subdiv Skeletal Component';

const SkeletalMeshesDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary max-w-[760px]">
      <DocHero
        title={t('subdivsDocs.layout.skeletalMeshes')}
        intro={
          <Trans
            i18nKey="subdivsDocs.skeletalMeshes.intro"
            components={{ comp: <>{COMPONENT}</> }}
          />
        }
        videoSrc="/videos/subdiv-skeletal-setup.mp4"
        videoCaption={t('subdivsDocs.skeletalMeshes.videoCaption', {
          comp: COMPONENT
        })}
      />

      <H2 id="adding">{t('subdivsDocs.layout.adding')}</H2>
      <Steps>
        <>{t('subdivsDocs.skeletalMeshes.step1')}</>
        <><Trans
            i18nKey="subdivsDocs.skeletalMeshes.step2"
            components={{
              add: M('Add'),
              q: <em>&ldquo;Squarebit Subdiv Skeletal&rdquo;</em>,
              comp: <strong>{COMPONENT}</strong>
            }}
          /></>
        <><Trans
            i18nKey="subdivsDocs.skeletalMeshes.step3"
            components={{ details: <strong>Details</strong> }}
          /></>
      </Steps>
      <Note label={t('subdivsDocs.skeletalMeshes.tipLabel')}>
        {t('subdivsDocs.skeletalMeshes.tipBody')}
      </Note>

      <H2 id="hotkeys">{t('subdivsDocs.skeletalMeshes.hotkeysTitle')}</H2>
      <Body>{t('subdivsDocs.skeletalMeshes.hotkeysBody')}</Body>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="subdivsDocs.skeletalMeshes.hotkey1"
            components={{ keys: <KeyCombo n="1" /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="subdivsDocs.skeletalMeshes.hotkey2"
            components={{ keys: <KeyCombo n="2" /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="subdivsDocs.skeletalMeshes.hotkey3"
            components={{ keys: <KeyCombo n="3" /> }}
          />
        </li>
      </ul>
      <Body>{t('subdivsDocs.skeletalMeshes.hotkeysNote')}</Body>

      <H2 id="multi-mesh">{t('subdivsDocs.layout.multiMesh')}</H2>
      <Body>{t('subdivsDocs.skeletalMeshes.multiMeshBody1')}</Body>
      <Body>
        <Trans
          i18nKey="subdivsDocs.skeletalMeshes.multiMeshBody2"
          components={{
            ssmc: <strong>Source Skeletal Mesh Components</strong>,
            pop: <strong>Populate Sources From Actor</strong>
          }}
        />
      </Body>
      {/* Label is the literal Unreal button name — never translated. */}
      <Note label="Populate Sources From Actor:">
        <Trans
          i18nKey="subdivsDocs.skeletalMeshes.populateBody"
          components={{ ssmc: <strong>Source Skeletal Mesh Components</strong> }}
        />
      </Note>
      <Body>
        <Trans
          i18nKey="subdivsDocs.skeletalMeshes.multiMeshBody3"
          components={{
            slpc: M('SetLeaderPoseComponent'),
            cpfm: M('CopyPoseFromMesh')
          }}
        />
      </Body>

      <KeySettings />
      <QuadCages />
      <SequencerNote />
      <DocFooter current="/squarebit-subdivs/docs/skeletal-meshes" />
    </article>
  );
};

export default SkeletalMeshesDoc;
